/**
 * SectionContextMenu — actions for an organ-subsection heading. Two
 * actions: "Rename section" (window.prompt, per-report / per-template) and
 * "Remove section" (window.confirm-gated). Shared by the case-viewer reading
 * flow AND the template editor (via useSectionContextMenu). Mirrors
 * ChipContextMenu's portal + outside-click / Esc dismissal and reuses its
 * `.chip-context-menu` styling so the menus look identical.
 *
 * Both editors open this menu by activating the heading or right-clicking it.
 * Removal still requires choosing the action and accepting confirmation.
 */

import { createPortal } from "react-dom";

import { useContextMenu } from "./useContextMenu";

export interface SectionContextMenuProps {
  anchor: { x: number; y: number };
  /** Section display label, e.g. "LIVER". */
  label: string;
  onRename: () => void;
  onRemove: () => void;
  onClose: () => void;
}

const MENU_WIDTH = 220;

export function SectionContextMenu({
  anchor,
  label,
  onRename,
  onRemove,
  onClose,
}: SectionContextMenuProps) {
  const { ref, style } = useContextMenu(anchor, onClose, MENU_WIDTH);

  return createPortal(
    <div
      ref={ref}
      className="chip-context-menu"
      role="menu"
      style={style}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        role="menuitem"
        type="button"
        className="chip-context-menu-item"
        onClick={() => {
          onRename();
          onClose();
        }}
      >
        <span>Rename {label} section</span>
      </button>
      <div className="chip-context-menu-divider" />
      <button
        role="menuitem"
        type="button"
        className="chip-context-menu-item chip-context-menu-item--destructive"
        onClick={() => {
          onRemove();
          onClose();
        }}
      >
        <span>Remove {label} section</span>
      </button>
    </div>,
    document.body,
  );
}
